import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon, ChevronRightIcon } from "lucide-react";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { WordReveal } from "../components/WordReveal";
import { AnimatedNumber } from "../components/AnimatedNumber";
import { MapleLeaf, MapleWatermark } from "../components/MapleLeaf";
import { COHORTS, HONOUR_THRESHOLD } from "../data/honourRow";
import type { Honouree } from "../data/honourRow";
import { ENROLMENT } from "../data/programmes";
import { IMAGES } from "../data/images";
import { EASE } from "../utils/motion";
const PREVIEW_TILES = 8;
function HonourTile({
  person,
  index
}: {
  person: Honouree;
  index: number;
}) {
  const reduce = useReducedMotion();
  return <motion.li layout initial={{
    opacity: 0,
    y: reduce ? 0 : 24
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} transition={{
    duration: reduce ? 0.01 : 0.5,
    delay: reduce ? 0 : Math.min(index * 0.05, 0.4),
    ease: EASE
  }} className="group" data-id="element-1230" data-mp="1xiu8akffg1o4">
      <div className="relative aspect-[3/4] overflow-hidden bg-[#061f38]" data-id="element-1231" data-mp="1yxgfcequt2i8">
        {person.image ? <img src={person.image} alt={person.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" data-id="element-1232" data-mp="1pylb387de2hk" /> : <span className="absolute inset-0 grid place-items-center text-6xl font-medium tracking-[-0.08em] text-[#f0b1b9]" data-id="element-1233" data-mp="2mmrj853i3d0l">{person.initials}</span>}
        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 bg-[#c5233d] px-2.5 py-1 text-xs font-semibold text-white" data-id="element-1234" data-mp="2dn4lpudw6rct">
          <MapleLeaf size={10} data-id="element-1235" data-mp="28r75rp6jjn0k" /> {person.average}%
        </span>
      </div>
      <p className="mt-4 text-lg font-medium tracking-[-0.03em]" data-id="element-1236" data-mp="12aljy765ca2g">{person.name}</p>
      {person.destination && <p className="text-sm text-[#4f5963]" data-id="element-1237" data-mp="x8mgkxaepd5i">{person.destination}</p>}
    </motion.li>;
}
function PreviewTile({
  index
}: {
  index: number;
}) {
  const reduce = useReducedMotion();
  return <motion.li initial={{
    opacity: 0,
    y: reduce ? 0 : 24
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} transition={{
    duration: reduce ? 0.01 : 0.5,
    delay: reduce ? 0 : Math.min(index * 0.05, 0.4),
    ease: EASE
  }} aria-hidden="true" data-id="element-1238" data-mp="13po0edqon54s">
      <div className="relative grid aspect-[3/4] place-items-center overflow-hidden border border-dashed border-[#cfcac0] bg-[#f2f0eb]" data-id="element-1239" data-mp="fzk832iy2rt3">
        <MapleLeaf size={44} className="text-[#dedbd3]" data-id="element-1240" data-mp="kteo9vzcconf" />
        <span className="absolute left-3 top-3 border border-[#cfcac0] px-2.5 py-1 text-xs font-semibold text-[#9aa1a6]" data-id="element-1241" data-mp="70yhql78e8hr">{HONOUR_THRESHOLD}%+</span>
      </div>
      <div className="mt-4 h-4 w-3/4 bg-[#e9e6df]" data-id="element-1242" data-mp="3t75672egeyo5" />
      <div className="mt-2 h-3 w-1/2 bg-[#efece6]" data-id="element-1243" data-mp="3t8g78ugilsbk" />
    </motion.li>;
}
export function HonourRow() {
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const cohort = COHORTS[active];
  const hasRecords = cohort.honourees.length > 0;
  const totalHonourees = COHORTS.reduce((n, c) => n + c.honourees.length, 0);
  return <>
      <section className="relative overflow-hidden bg-[#061f38] pb-20 pt-32 text-white md:pb-28 md:pt-40" data-id="element-1244" data-mp="3oj5gs75ux1ng">
        <MapleWatermark className="-right-32 -top-10 h-[46rem] w-[46rem]" tone="text-white/[0.04]" rotate={-14} data-id="element-1245" data-mp="3jj9lqxf50kk1" />
        <div className="relative mx-auto max-w-[1440px] px-5 md:px-9" data-id="element-1246" data-mp="1cv16fmd5cco">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-white/60" data-id="element-1247" data-mp="3skqzi952oa5k">
            <Link to="/" className="hover:text-white" data-id="element-1248" data-mp="b495d0p9f0c5">Home</Link>
            <ChevronRightIcon size={13} data-id="element-1249" data-mp="b5sor067pri5" />
            <Link to="/academics" className="hover:text-white" data-id="element-1250" data-mp="64czevx04yty">Academics</Link>
            <ChevronRightIcon size={13} data-id="element-1251" data-mp="k44lljxo9lj1" />
            <span className="text-white/90" data-id="element-1252" data-mp="k57chruzvnyy">Honour Row</span>
          </nav>
          <div className="mt-14 grid items-end gap-10 lg:grid-cols-12" data-id="element-1253" data-mp="g3lpvesnrvbk">
            <div className="lg:col-span-7" data-id="element-1254" data-mp="29ljaft6vur6b">
              <p className="text-xs font-semibold tracking-[0.2em] text-[#f0b1b9]" data-id="element-1255" data-mp="29kqd1ittde0u">THE HONOUR ROW · SINCE {ENROLMENT.founded}</p>
              <h1 className="mt-6 text-[clamp(3.2rem,8vw,7.5rem)] font-medium leading-[0.88] tracking-[-0.075em]" data-id="element-1256" data-mp="2eijho7fgxzz0">Ninety and above.</h1>
              <p className="mt-8 max-w-md text-lg leading-8 text-white/70" data-id="element-1257" data-mp="2oa5yqkehmt9o">Every student who has completed MCC with an overall average of {HONOUR_THRESHOLD}% or higher, honoured by name, year after year.</p>
            </div>
            <motion.p initial={{
            opacity: 0,
            y: reduce ? 0 : 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: reduce ? 0.01 : 0.9,
            delay: reduce ? 0 : 0.2,
            ease: EASE
          }} className="flex items-start text-[clamp(8rem,20vw,18rem)] font-medium leading-[0.78] tracking-[-0.09em] text-white lg:col-span-5 lg:justify-self-end" data-id="element-1258" data-mp="1vmtewhzpozk2">
              {HONOUR_THRESHOLD}
              <span className="mt-[0.08em] text-[0.4em] text-[#c5233d]" data-id="element-1259" data-mp="3g3olvsetpriv">+</span>
            </motion.p>
          </div>
        </div>
      </section>

      <main data-id="element-1260" data-mp="10c50upbyi7gl">
        <section className="mx-auto grid max-w-[1440px] gap-12 px-5 py-24 md:px-9 lg:grid-cols-12 lg:py-32" data-id="element-1261" data-mp="10dpsa62z5aa8">
          <div className="lg:col-span-7" data-id="element-1262" data-mp="vkn8uqrwvh7r">
            <WordReveal as="p" text="A place on the Honour Row is earned over a full programme of coursework, assessments and exams. It is the highest academic recognition MCC gives." className="text-3xl font-medium leading-[1.12] tracking-[-0.045em] md:text-4xl" data-id="element-1263" data-mp="vjlm3p1j96a7" />
          </div>
          <div className="grid grid-cols-2 content-end gap-6 border-t border-[#061f38] pt-6 lg:col-span-4 lg:col-start-9" data-id="element-1264" data-mp="gncqqykrjd32">
            <Reveal data-id="element-1265" data-mp="7nze5x1ce8e4">
              <p className="text-5xl font-medium tracking-[-0.07em]" data-id="element-1266" data-mp="7oitr73iskoz"><AnimatedNumber value={COHORTS.length} data-id="element-1267" data-mp="7q2dj7odxdvi" /></p>
              <p className="mt-2 text-sm text-[#4f5963]" data-id="element-1268" data-mp="270249q4h210r">graduating classes</p>
            </Reveal>
            <Reveal delay={0.1} data-id="element-1269" data-mp="2231xte3pmw94">
              <p className="text-5xl font-medium tracking-[-0.07em] text-[#bb1e35]" data-id="element-1270" data-mp="222s4bgjgi6yo"><AnimatedNumber value={ENROLMENT.value} suffix={ENROLMENT.suffix} data-id="element-1271" data-mp="2l5kjd0bvbu6j" /></p>
              <p className="mt-2 text-sm text-[#4f5963]" data-id="element-1272" data-mp="2pxlrqb5x10ra">students since {ENROLMENT.founded}</p>
            </Reveal>
          </div>
        </section>

        <section className="pb-24 md:pb-32" data-id="element-1273" data-mp="2c3ujd63x7mx5">
          <div className="sticky top-20 z-20 border-y border-[#dedbd3] bg-[#fbfaf6]/95 backdrop-blur-sm" data-id="element-1274" data-mp="2h48s6qdcj0w8">
            <div role="tablist" aria-label="Graduating class" className="no-scrollbar mx-auto flex max-w-[1440px] gap-1 overflow-x-auto px-5 md:px-9" data-id="element-1275" data-mp="34ucsanrbsiih">
              {COHORTS.map((c, i) => <button key={c.year} role="tab" aria-selected={active === i} onClick={() => setActive(i)} className={`relative shrink-0 px-4 py-4 text-sm font-medium tabular-nums transition-colors duration-200 md:px-6 md:text-base ${active === i ? "text-[#061f38]" : "text-[#9aa1a6] hover:text-[#061f38]"}`} data-id="element-1276" data-mp="2ux87ar97ut39" data-mpi={i}>
                  Class of {c.year}
                  {active === i && <motion.span layoutId="honour-year" transition={{
                duration: reduce ? 0 : 0.35,
                ease: EASE
              }} className="absolute inset-x-3 bottom-0 h-[3px] bg-[#c5233d]" data-id="element-1277" data-mp="1bxr3xkzcgre5" />}
                </button>)}
            </div>
          </div>

          <div className="mx-auto max-w-[1440px] px-5 pt-12 md:px-9 md:pt-16" data-id="element-1278" data-mp="1kzqlqfi6nzi5">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end" data-id="element-1279" data-mp="x831uhjc9opd">
              <AnimatePresence mode="wait" data-id="element-1280" data-mp="t7stiy82qm7c">
                <motion.h2 key={cohort.year} initial={{
                opacity: 0,
                y: reduce ? 0 : 16
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: reduce ? 0 : -10
              }} transition={{
                duration: reduce ? 0.01 : 0.35,
                ease: EASE
              }} className="text-5xl font-medium tracking-[-0.07em] md:text-7xl" data-id="element-1281" data-mp="t94015swm825">
                  {cohort.year}
                </motion.h2>
              </AnimatePresence>
              <p className="max-w-sm text-sm leading-6 text-[#4f5963]" data-id="element-1282" data-mp="9iifdonz89bc">
                {hasRecords ? `${cohort.honourees.length} students finished this year at ${HONOUR_THRESHOLD}% or above.` : "Names for this class are being compiled from MCC records and will appear here with each student’s consent."}
              </p>
            </div>

            <AnimatePresence mode="wait" data-id="element-1283" data-mp="3u5vj79ntm4qw">
              <motion.ul key={cohort.year} className="mt-12 grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-3 lg:grid-cols-4" data-id="element-1284" data-mp="2xhnuhamkop1">
                {hasRecords ? cohort.honourees.map((p, i) => <HonourTile key={p.name} person={p} index={i} data-id="element-1285" data-mp="bx4h2ft7ao70" data-mpi={i} />) : Array.from({
                length: PREVIEW_TILES
              }, (_, i) => <PreviewTile key={i} index={i} data-id="element-1286" data-mp="gpnxdkj9vxfi" />)}
              </motion.ul>
            </AnimatePresence>
            {totalHonourees === 0 && <p className="mt-10 flex items-center gap-2 text-xs text-[#6b7378]" data-id="element-1287" data-mp="lpjxv9srh6qb"><MapleLeaf size={11} className="text-[#bb1e35]" data-id="element-1288" data-mp="qoddp8vit9tn" /> Preview layout. Honour Row records from 2019 to date will be supplied by MCC.</p>}
          </div>
        </section>

        <section className="relative overflow-hidden bg-[#f2f0eb] py-20 md:py-28" data-id="element-1289" data-mp="2g4pdzguvsp46">
          <MapleWatermark className="-left-20 -bottom-24 h-[26rem] w-[26rem]" tone="text-[#061f38]/[0.04]" rotate={10} data-id="element-1290" data-mp="2b3tcf9y05f1x" />
          <div className="relative mx-auto flex max-w-[1440px] flex-col gap-8 px-5 md:flex-row md:items-end md:justify-between md:px-9" data-id="element-1291" data-mp="2669xs8xe709g">
            <div data-id="element-1292" data-mp="2yufjoz06mvav">
              <p className="text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-1293" data-mp="2yuyrvo0165bp">WHAT COMES NEXT</p>
              <h2 className="mt-5 max-w-xl text-4xl font-medium leading-[0.95] tracking-[-0.06em] md:text-5xl" data-id="element-1294" data-mp="2tusx3yygs2pe">Strong results open doors. See where they lead.</h2>
            </div>
            <Link to="/academics/scholarships" className="group inline-flex w-fit items-center gap-3 bg-[#061f38] px-5 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#0c2c4d]" data-id="element-1295" data-mp="2k5j2abc8k5xl">
              <MapleLeaf size={13} className="text-[#f0b1b9]" data-id="element-1296" data-mp="3cughu6p2bomn" /> Scholarships &amp; results <ArrowRightIcon size={16} className="transition-transform duration-200 group-hover:translate-x-1" data-id="element-1297" data-mp="3csdk10ho4r7d" />
            </Link>
          </div>
        </section>
      </main>

      <CTABand eyebrow="START HERE" title="The next name on the Honour Row could be your child’s." image={IMAGES.graduation} data-id="element-1298" data-mp="10omyfx5e2blf" />
    </>;
}